import { API_URL } from './constants';
import {
  ApiMarketingCampaignGetContactsQuery,
  ApiMarketingCampaignGetContactsResponse,
  ApiMarketingCampaignGetSettingsResponse,
  ApiMarketingCampaignImportCsvResponse,
  ApiMarketingCampaignSendTestEmailRequest,
  ApiMarketingCampaignSendTestEmailResponse,
  ApiMarketingCampaignUpdateSettingsRequest,
  MarketingCampaignSettings,
} from './types/marketing-campaign';

function authHeaders(token: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function apiGetMarketingCampaignSettings(
  token: string,
): Promise<[ApiMarketingCampaignGetSettingsResponse, number]> {
  const url = new URL(
    `${API_URL}/admin/marketing-campaign/get-settings`,
  ).href;
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(token),
  });
  return [
    (await response.json()) as ApiMarketingCampaignGetSettingsResponse,
    response.status,
  ];
}

export async function apiUpdateMarketingCampaignSettings(
  token: string,
  body: ApiMarketingCampaignUpdateSettingsRequest,
): Promise<[MarketingCampaignSettings, number]> {
  const url = new URL(
    `${API_URL}/admin/marketing-campaign/update-settings`,
  ).href;
  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return [(await response.json()) as MarketingCampaignSettings, response.status];
}

async function postCampaignAction(
  token: string,
  action: 'start' | 'pause' | 'stop',
): Promise<[MarketingCampaignSettings, number]> {
  const url = new URL(`${API_URL}/admin/marketing-campaign/${action}`).href;
  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return [(await response.json()) as MarketingCampaignSettings, response.status];
}

export async function apiStartMarketingCampaign(token: string) {
  return postCampaignAction(token, 'start');
}

export async function apiPauseMarketingCampaign(token: string) {
  return postCampaignAction(token, 'pause');
}

export async function apiStopMarketingCampaign(token: string) {
  return postCampaignAction(token, 'stop');
}

export async function apiGetMarketingCampaignContacts(
  token: string,
  query: ApiMarketingCampaignGetContactsQuery,
): Promise<[ApiMarketingCampaignGetContactsResponse, number]> {
  const url = new URL(`${API_URL}/admin/marketing-campaign/get-contacts`);
  if (query.status) url.searchParams.set('status', query.status);
  if (query.currentStep !== undefined)
    url.searchParams.set('currentStep', String(query.currentStep));
  if (query.offset !== undefined)
    url.searchParams.set('offset', String(query.offset));
  if (query.limit !== undefined)
    url.searchParams.set('limit', String(query.limit));

  const response = await fetch(url.href, {
    method: 'GET',
    headers: authHeaders(token),
  });
  return [
    (await response.json()) as ApiMarketingCampaignGetContactsResponse,
    response.status,
  ];
}

export async function apiImportMarketingCampaignCsv(
  token: string,
  file: File,
): Promise<[ApiMarketingCampaignImportCsvResponse, number]> {
  const formData = new FormData();
  formData.append('file', file);

  const url = new URL(`${API_URL}/admin/marketing-campaign/import-csv`).href;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  return [
    (await response.json()) as ApiMarketingCampaignImportCsvResponse,
    response.status,
  ];
}

export async function apiSendTestMarketingCampaignEmail(
  token: string,
  body: ApiMarketingCampaignSendTestEmailRequest,
): Promise<[ApiMarketingCampaignSendTestEmailResponse, number]> {
  const url = new URL(
    `${API_URL}/admin/marketing-campaign/send-test-email`,
  ).href;
  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return [
    (await response.json()) as ApiMarketingCampaignSendTestEmailResponse,
    response.status,
  ];
}

export async function apiDeleteMarketingContact(
  token: string,
  contactId: string,
): Promise<[{ success: boolean }, number]> {
  const url = new URL(`${API_URL}/admin/marketing-campaign/delete-contact`);
  url.searchParams.set('contactId', contactId);

  const response = await fetch(url.href, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return [(await response.json()) as { success: boolean }, response.status];
}
